import 'isomorphic-fetch'
import { API, createDefaultHeaders } from '../../util/api'
import { getResolvedUserId } from '../../util/maskAsUser'
import { isInvalid } from '@rachio-npm/com-rachio-web-core/util/validation'
import { UUIDSchema } from '../../util/schema'
import * as actionCreators from './creators'
import * as userSchema from './schemas'
import changeCase from 'change-object-case'

/**
 * A module that exports `create`, `get`, and `update` functions
 * @module ducks/user/crud
 */

/**
 * Create user
 * @param {Object} userData - User's data
 * @param {string} userData.firstName - User's firstname
 * @param {string} userData.lastName - User's lastname
 * @param {string} userData.emailAddress - User's email address
 * @param {string} userData.password - User's desired password
 * @returns {Promise.dispatch} Dispatches `userRequestSuccess` action
 */
export function create(userData) {
  return function(dispatch) {
    const Api = new API()
    const reqData = changeCase.snakeKeys(userData, { recursive: true })
    const error = isInvalid(userSchema.UserCreateRequestSchema, reqData)
    const headers = { 'Content-Type': 'application/json' }

    dispatch(actionCreators.userCreateInit(reqData))

    if (error) {
      return Promise.reject(dispatch(actionCreators.userCreateComplete(error)))
    } else {
      return Api.call('user/createUser', {
        method: 'POST',
        body: JSON.stringify(reqData),
        headers: headers
      })
        .then(response => {
          dispatch(actionCreators.userCreateComplete(reqData))
          return response
        })
        .then(response => {
          if (response && response.status === 409) {
            throw new Error('Email address exist in system')
          }
          return response
        })
        .then(response =>
          response && response.json
            ? response
                .json()
                .then(json => dispatch(actionCreators.userCreateComplete(json)))
            : response
        )
        .catch(err => dispatch(actionCreators.userCreateComplete(err)))
    }
  }
}

/**
 * Get user
 * @param {string} bearer - User's bearer token
 * @param {string} currentUserId - User's id
 * @returns {Promise.dispatch} Dispatches `userRequestComplete` action
 */
export function get(bearer, currentUserId) {
  return function(dispatch) {
    const userId = getResolvedUserId(currentUserId)
    const Api = new API()
    const error = isInvalid(UUIDSchema, userId)
    const headers = createDefaultHeaders(bearer)

    dispatch(actionCreators.userRequestInit(userId))

    if (error) {
      return Promise.reject(dispatch(actionCreators.userRequestComplete(error)))
    } else {
      return Api.call('user/getUser/' + userId, { headers: headers })
        .then(response => response.json())
        .then(json =>
          dispatch(
            actionCreators.userRequestComplete({
              ...json,
              ...{ lastPulled: Date.now() }
            })
          )
        )
        .catch(err => dispatch(actionCreators.userRequestComplete(err)))
    }
  }
}

/**
 * Update user
 * @todo Add validations
 * @param {string} bearer - User's bearer token
 * @param {string} userId - User's id
 * @param {Object} updateObj - Fields and values to update
 * @returns {Promise.dispatch} Dispatches `userRequestSuccess` action
 */
export function update(bearer, userId, updateObj) {
  return function(dispatch) {
    const Api = new API()
    const reqData = changeCase.snakeKeys(
      { ...updateObj, userId: userId },
      { recursive: true }
    )
    const headers = {
      ...createDefaultHeaders(bearer),
      'Content-Type': 'application/json'
    }
    const error = isInvalid(userSchema.UserUpdateRequestSchema, reqData)
    let errorAction

    dispatch(
      actionCreators.userUpdateInit({ userId: userId, updateObj: updateObj })
    )

    if (error) {
      errorAction = {
        ...actionCreators.userUpdateComplete(error),
        meta: { userId: userId }
      }
      return Promise.reject(dispatch(errorAction))
    } else {
      return Api.call('user/updateUser', {
        method: 'PUT',
        body: JSON.stringify(reqData),
        headers: headers
      })
        .then(response => {
          return response.json()
        })
        .then(json => {
          dispatch(actionCreators.set(json.user))
          return dispatch(
            actionCreators.userUpdateComplete({
              ...json,
              ...{ lastPulled: Date.now() }
            })
          )
        })
        .catch(err => {
          errorAction = actionCreators.userUpdateComplete(err)
          errorAction.meta = { userId: userId }
          return dispatch(errorAction)
        })
    }
  }
}
